import { canStartEdge, KEAElement, KEALink, KEAPaper, keaNamespace } from "@kea-mod/jointjs";
import { InteractionType } from "context/KEAGraphContext";
import * as joint from "jointjs";
import { useEffect, useRef } from "react";

export type AppendTarget = {
  element: joint.dia.Element;
  paper: KEAPaper;
};

interface Props {
  target: AppendTarget;
  pushPresent: () => void;
  addUserInteraction: (interactionType: InteractionType, interactionTime: number, payload?: any) => void;
}

const THUMB = 28;
const COLUMNS = 3;
const CELL_GAP = 3;
const PADDING = 4;
const GAP = 120;

const PALETTE_WIDTH = COLUMNS * THUMB + (COLUMNS - 1) * CELL_GAP + 2 * PADDING;

// The sample texts of the templates enlarge the bounding box and shrink the symbol itself.
const withoutLabel = (template: joint.dia.Element): joint.dia.Element => {
  const clone = template.clone();
  const element = clone as unknown as KEAElement & { setClassName?: (name: string) => void };
  if (typeof element.setLabel === "function") element.setLabel("");
  // The UML class keeps its name separate from the label and inflates the preview the same way.
  if (typeof element.setClassName === "function") element.setClassName("");
  return clone;
};

const Thumbnail = ({ template, onPick }: { template: joint.dia.Element; onPick: () => void }): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    // Own container: if JointJS clears the node React manages, React throws NotFoundError on unmount.
    const host = document.createElement("div");
    ref.current.appendChild(host);
    const graph = new joint.dia.Graph({}, { cellNamespace: keaNamespace });
    const paper = new joint.dia.Paper({
      el: host,
      model: graph,
      width: THUMB,
      height: THUMB,
      interactive: false,
      background: { color: "transparent" },
    });
    graph.addCell(withoutLabel(template).position(0, 0));
    // The bounding box includes the label, the element size ignores borders and tips, so use both.
    const size = template.size();
    const factor = size.width > 0 && size.height > 0 ? Math.min(THUMB / size.width, THUMB / size.height) : 1;
    paper.scale(factor, factor);

    const drawn = paper.getContentBBox();
    if (drawn.width > 0 && drawn.height > 0) {
      const corrected = factor * Math.min(THUMB / drawn.width, THUMB / drawn.height) * 0.88;
      paper.scale(corrected, corrected);
      const box = paper.getContentBBox();
      const origin = paper.translate();
      paper.translate(
        origin.tx + (THUMB - box.width) / 2 - box.x,
        origin.ty + (THUMB - box.height) / 2 - box.y,
      );
    }
    return () => {
      paper.remove();
      host.remove();
    };
  }, [template]);

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      onMouseDown={(e) => {
        // A right click that only happens to hit the tile must not append.
        if (e.button !== 0) return;
        e.stopPropagation();
        onPick();
      }}
      onContextMenu={(e) => e.preventDefault()}
      onKeyDown={(e) => e.key === "Enter" && onPick()}
      style={{ width: THUMB, height: THUMB, cursor: "pointer" }}
    />
  );
};

export const AppendPalette = ({ target, pushPresent, addUserInteraction }: Props): JSX.Element | null => {
  const { element, paper } = target;
  const templates = paper.appendTemplates ?? [];

  if (templates.length === 0) return null;

  const bbox = element.getBBox();
  const at = paper.localToPaperPoint(bbox.x + bbox.width, bbox.y);

  // At the right edge the grid would otherwise run out of the canvas.
  const scale = paper.scale().sx;
  // The eastern connection arrow sticks out 9 px past the element border and would be covered.
  const OFFSET = 28;
  const fitsRight = at.x + OFFSET + PALETTE_WIDTH <= paper.getComputedSize().width;
  const left = fitsRight ? at.x + OFFSET : Math.max(4, at.x - bbox.width * scale - OFFSET - PALETTE_WIDTH);

  const append = (template: joint.dia.Element) => {
    const created = template.clone() as unknown as KEAElement;
    const size = created.size();
    created.position(bbox.x + bbox.width + GAP, bbox.y + (bbox.height - size.height) / 2);
    paper.model.addCell(created);

    const link = paper.newLinkWithLastVariant();
    link.set("source", paper.connectionEnd(element as unknown as KEAElement));
    link.set("target", paper.connectionEnd(created));
    paper.model.addCell(link as unknown as KEALink);

    pushPresent();
    addUserInteraction(InteractionType.NODE_CREATED, Date.now(), created.toJSON());

    // Keep the bar on the new element so chains can be built in one go.
    paper.selectNewElement(created as unknown as joint.dia.Cell);
    const view = paper.findViewByModel(created as unknown as joint.dia.Cell) as joint.dia.ElementView;
    if (view && canStartEdge(created.get("type"))) paper.trigger("keaelement:append", view);
  };

  return (
    <div
      className="box"
      style={{
        position: "absolute",
        left,
        top: at.y,
        padding: PADDING,
        display: "grid",
        gridTemplateColumns: `repeat(${COLUMNS}, ${THUMB}px)`,
        gap: CELL_GAP,
        width: PALETTE_WIDTH,
        zIndex: 15,
      }}
    >
      {templates.map((template) => (
        <Thumbnail key={template.id} template={template} onPick={() => append(template)} />
      ))}
    </div>
  );
};
